import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeTab) || categories[0];
  const filteredArticles = articles.filter((a) => a.categorySlug === activeTab);

  return (
    <div className="min-h-screen bg-background-50">
      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: activeCategory.name },
        ]}
      />

      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        <div className="mb-10">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            {activeCategory.name}
          </h1>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
            {activeCategory.description}
          </p>
        </div>

        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              onClick={() => setActiveTab(cat.slug)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                activeTab === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
              <span className={`text-xs ml-1 ${activeTab === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                {cat.articleCount}
              </span>
            </Link>
          ))}
        </div>

        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} - ${siteInfo.name}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-md">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{article.readTime}</span>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    <div className="flex items-center gap-3">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs text-secondary-500">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无文章</p>
          </div>
        )}
      </section>
    </div>
  );
}